import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../../components/Navbar";
import RolesTable from "../../../components/Roles/RolesTable";
import { motion } from "framer-motion";
import {
  Shield,
  Users,
  ChevronRight,
  Plus,
  Settings,
  UserCheck,
  UserX,
  AlertCircle,
  Crown,
  Briefcase,
  Eye,
  Edit,
  Trash2,
  Lock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
} from "lucide-react";
import { useTeamManagement } from "../../../hooks/permissionHooks/useTeamManagement";
import { useCustomRoles } from "../../../hooks/permissionHooks/useCustomRoles";
import { useTeamValidation } from "../../../hooks/permissionHooks/useTeamValidation";
import Loader from "../../../components/Loader";
import { toast } from "react-toastify";
import { usePermissionCheck } from "../../../hooks/permissionHooks/usePermissionCheck";
import { useSelector } from "react-redux";
import { PERMISSIONS } from "../../../config/permissions";
import PermissionAwareButton from "../../../components/Permissions/PermissionAwareButton";
import RolesPermissionTest from "../../../components/Permissions/RolesPermissionTest";

const StatCard = ({
  icon: Icon,
  title,
  value,
  subtitle,
  color = "blue",
  onClick,
}) => {
  const colorClasses = {
    blue: "from-blue-500 to-blue-600 bg-blue-50 text-blue-500",
    green: "from-green-500 to-green-600 bg-green-50 text-green-500",
    red: "from-red-500 to-red-600 bg-red-50 text-red-500",
    purple: "from-purple-500 to-purple-600 bg-purple-50 text-purple-500",
    orange: "from-orange-500 to-orange-600 bg-orange-50 text-orange-500",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={onClick}
      className={`p-6 bg-white rounded-xl border border-gray-100 shadow-sm transition-all hover:shadow-md ${
        onClick ? "cursor-pointer hover:border-gray-200" : ""}`}
    >
      <div className="flex justify-between items-center">
        <div>
          <p className="mb-1 text-sm font-medium text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-800">{value}</p>
          {subtitle && <p className="mt-1 text-xs text-gray-500">{subtitle}</p>}
        </div>
        <div
          className={`p-3 rounded-xl ${colorClasses[color]
            .split(" ")
            .slice(2)
            .join(" ")}`}
        >
          <Icon className={`w-6 h-6 ${colorClasses[color].split(" ")[3]}`} />
        </div>
      </div>
    </motion.div>
  );
};

const EmptyState = ({
  title,
  description,
  actionText,
  onAction,
  icon: Icon,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex flex-col justify-center items-center p-12 text-center bg-white rounded-xl border border-gray-100"
  >
    <div className="p-4 mb-4 bg-gray-50 rounded-full">
      <Icon className="w-8 h-8 text-gray-400" />
    </div>
    <h3 className="mb-2 text-lg font-semibold text-gray-800">{title}</h3>
    <p className="mb-6 max-w-sm text-gray-500">{description}</p>
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onAction}
      className="px-6 py-3 text-white bg-gradient-to-r from-[#FF6C6C] to-pink-500 rounded-xl hover:from-[#FF6C6C]/90 hover:to-pink-600 transition-all duration-200 font-medium"
    >
      {actionText}
    </motion.button>
  </motion.div>
);

const Roles = () => {
  const navigate = useNavigate();
  
  // Get user from Redux store
  const user = useSelector((state) => state.user);
  
  // Enhanced permission checks
  const { 
    canPerform, 
    isAuthenticated,
    isMainVendor,
    userPermissions 
  } = usePermissionCheck();

  // Check specific role permissions
  const canViewRoles = canPerform(PERMISSIONS.MODULES.ROLES_PERMISSION, PERMISSIONS.ACTIONS.ROLE_VIEW) || isMainVendor;
  const canCreateRoles = canPerform(PERMISSIONS.MODULES.ROLES_PERMISSION, PERMISSIONS.ACTIONS.ROLE_CREATE) || isMainVendor;
  const canEditRoles = canPerform(PERMISSIONS.MODULES.ROLES_PERMISSION, PERMISSIONS.ACTIONS.ROLE_EDIT) || isMainVendor;
  const canDeleteRoles = canPerform(PERMISSIONS.MODULES.ROLES_PERMISSION, PERMISSIONS.ACTIONS.ROLE_DELETE) || isMainVendor;

  // Debug logging for permission checks
  console.log('Roles Permission Debug:', {
    isAuthenticated,
    isMainVendor,
    canViewRoles,
    canCreateRoles,
    canEditRoles,
    canDeleteRoles,
    userPermissions,
    userRole: user?.userRole,
    PERMISSIONS_MODULE: PERMISSIONS.MODULES.ROLES_PERMISSION,
    PERMISSIONS_ACTIONS: [
      PERMISSIONS.ACTIONS.ROLE_VIEW,
      PERMISSIONS.ACTIONS.ROLE_CREATE,
      PERMISSIONS.ACTIONS.ROLE_EDIT,
      PERMISSIONS.ACTIONS.ROLE_DELETE
    ]
  });

  // State for permission test
  const [showPermissionTest, setShowPermissionTest] = useState(false);

  // Hooks
  const {
    teamMembers,
    customRoles,
    isLoading,
    isFetchingMembers,
    hasRoles,
    canCreateMembers,
    requiresRoles,
  } = useTeamManagement();

  const { totalRoles } = useCustomRoles();
  const { canCreateTeamMembers } = useTeamValidation();

  // Local state
  const [selectedView, setSelectedView] = useState("team"); // 'team' or 'roles'

  // Check creation permissions
  const { canCreate: canCreateMembersCheck, reason: cannotCreateReason } =
    canCreateTeamMembers();

  // Statistics
  const teamStats = {
    total: teamMembers.length,
    active: teamMembers.filter((m) => m.status === "active" || m.status === "Active").length,
    inactive: teamMembers.filter((m) => m.status === "inactive" || m.status === "Inactive").length,
    roles: totalRoles,
  };

  // Handlers
  const handleCreateTeamMember = () => {
    if (!canCreateRoles) {
      toast.error("You don't have permission to create team members");
      return;
    }
    if (!canCreateMembersCheck) {
      toast.error(cannotCreateReason);
      return;
    }
    navigate("/roles/team-member/create");
  };

  const handleManageRoles = () => {
    if (!canViewRoles) {
      toast.error("You don't have permission to view roles");
      return;
    }
    navigate("/roles/manage");
  };

  const handleCreateRole = () => {
    if (!canCreateRoles) {
      toast.error("You don't have permission to create roles");
      return;
    }
    navigate("/roles/create");
  };

  const handleEditTeamMember = (teamMember) => {
    if (!canEditRoles) {
      toast.error("You don't have permission to edit team members");
      return;
    }
    navigate(`/roles/team-member/edit/${teamMember.id}`);
  };

  // Access denied screen for users without view permissions
  if (!isAuthenticated || !canViewRoles) {
    return (
      <div className="bg-white">
        <Navbar page="Roles & Permissions" />
        <div className="flex justify-center items-center min-h-[80vh] bg-gray-50">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-8 max-w-md text-center"
          >
            <div className="inline-block p-4 mb-6 bg-red-100 rounded-full">
              <Shield className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="mb-4 text-2xl font-bold text-gray-800">Access Restricted</h2>
            <p className="mb-6 leading-relaxed text-gray-600">
              You don't have permission to view roles and permissions. Please contact your administrator to request access to the Roles & Permissions module.
            </p>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex gap-2 items-center mb-2">
                <AlertTriangle className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Required Permission:</span>
              </div>
              <p className="text-sm text-blue-700">Role_View in Roles & Permissions</p>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/dashboard')}
              className="mt-6 px-6 py-3 bg-[#FF6C6C] text-white rounded-lg hover:bg-[#FF6C6C]/90 transition-colors font-medium"
            >
              Return to Dashboard
            </motion.button>
          </motion.div>
        </div>
      </div>
    );
  }

  // Loading state
  if (isFetchingMembers && teamStats.total === 0) {
    return <Loader />;
  }

  return (
    <div className="bg-white">
      <Navbar page={"Roles & Permissions"} />

      <div className="p-6 min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="mb-8">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex gap-2 items-center mb-4 text-sm text-gray-500"
          >
            <span
              className="hover:text-[#FF6C6C] cursor-pointer transition-colors"
              onClick={() => navigate("/dashboard")}
            >
              Dashboard
            </span>
            <ChevronRight size={16} />
            <span className="text-[#FF6C6C] font-medium">
              Roles & Permissions
            </span>
          </motion.div>

          {/* Header */}
          <div className="flex flex-col gap-6 justify-between p-4 bg-white rounded-2xl border border-gray-100 shadow-sm lg:flex-row lg:items-center md:p-6">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex flex-col gap-4 sm:flex-row sm:items-center"
            >
              <motion.div
                whileHover={{ rotate: 15, scale: 1.1 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
                className="p-3 md:p-4 rounded-2xl bg-gradient-to-br from-[#FF6C6C]/10 via-pink-50 to-white shadow-lg shadow-[#FF6C6C]/5"
              >
                <Shield className="w-6 h-6 md:w-8 md:h-8 text-[#FF6C6C]" />
              </motion.div>
              <div>
                <motion.h1
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="text-2xl font-bold tracking-tight text-gray-800 md:text-3xl"
                >
                  Roles & Permissions
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="mt-1 text-sm text-gray-500 md:text-base"
                >
                  Manage user roles and access control for your organization
                </motion.p>
              </div>
            </motion.div>

            {/* Action Buttons - Updated with permission checks */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex flex-col gap-3 items-stretch sm:flex-row sm:items-center"
            >
              {/* Permission Test Button */}
              {/* <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowPermissionTest(!showPermissionTest)}
                className="px-3 py-2 text-sm text-white bg-blue-500 rounded-lg transition-colors hover:bg-blue-600"
              >
                {showPermissionTest ? 'Hide' : 'Show'} Permission Test
              </motion.button> */}

              {canViewRoles && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleManageRoles}
                  className="flex gap-2 justify-center items-center px-4 py-2.5 text-sm font-medium text-gray-600 bg-white rounded-xl border border-gray-200 transition-all duration-200 hover:bg-gray-50 hover:border-gray-300"
                >
                  <Settings size={18} />
                  Manage Roles
                </motion.button>
              )}

              {canCreateRoles && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleCreateTeamMember}
                  disabled={!canCreateMembersCheck}
                  className="px-4 py-2.5 rounded-xl text-white bg-gradient-to-r from-[#FF6C6C] to-pink-500 hover:from-[#FF6C6C]/90 hover:to-pink-600 shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center gap-2 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  title={!canCreateMembersCheck ? cannotCreateReason : ""}
                >
                  <Plus size={18} />
                  Add Team Member
                </motion.button>
              )}
            </motion.div>
          </div>
        </div>

        {/* Permission Test Section */}
        {showPermissionTest && (
          <RolesPermissionTest />
        )}

        {/* Temporary Debug Section */}
        {/* <div className="p-4 mb-6 bg-yellow-50 rounded-lg border border-yellow-200">
          <h3 className="font-medium text-yellow-800 mb-2">Navigation Debug Info</h3>
          <div className="text-sm text-yellow-700 space-y-1">
            <p><strong>User Role:</strong> {user?.userRole || 'Not set'}</p>
            <p><strong>Is Main Vendor:</strong> {user?.is_main_vendor ? 'Yes' : 'No'}</p>
            <p><strong>User Permissions:</strong> {JSON.stringify(user?.permissions, null, 2)}</p>
            <p><strong>Required for Navigation:</strong> Role_View in Roles_Permission</p>
            <p><strong>Has Role_View:</strong> {canViewRoles ? 'Yes' : 'No'}</p>
            <p><strong>Navigation Should Show:</strong> {canViewRoles ? 'Yes' : 'No'}</p>
          </div>
          <button
            onClick={() => {
              console.log('Manual Navigation Debug:', {
                user,
                canViewRoles,
                userPermissions: user?.permissions,
                requiredModule: PERMISSIONS.MODULES.ROLES_PERMISSION,
                requiredAction: PERMISSIONS.ACTIONS.ROLE_VIEW
              });
            }}
            className="mt-2 px-3 py-1 bg-yellow-600 text-white rounded text-xs"
          >
            Log Navigation Debug
          </button>
        </div> */}

        {/* Permission Status Cards */}
        {/* <div className="grid grid-cols-1 gap-4 mb-8 md:grid-cols-2 lg:grid-cols-4">
          <div className={`p-4 rounded-lg border ${canViewRoles ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex gap-3 items-center">
              {canViewRoles ? <CheckCircle className="w-5 h-5 text-green-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
              <div>
                <p className="text-sm font-medium">View Roles</p>
                <p className="text-xs text-gray-500">{canViewRoles ? 'Access granted' : 'Access denied'}</p>
              </div>
            </div>
          </div>
          
          <div className={`p-4 rounded-lg border ${canCreateRoles ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex gap-3 items-center">
              {canCreateRoles ? <CheckCircle className="w-5 h-5 text-green-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
              <div>
                <p className="text-sm font-medium">Create Roles</p>
                <p className="text-xs text-gray-500">{canCreateRoles ? 'Access granted' : 'Access denied'}</p>
              </div>
            </div>
          </div>
          
          <div className={`p-4 rounded-lg border ${canEditRoles ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex gap-3 items-center">
              {canEditRoles ? <CheckCircle className="w-5 h-5 text-green-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
              <div>
                <p className="text-sm font-medium">Edit Roles</p>
                <p className="text-xs text-gray-500">{canEditRoles ? 'Access granted' : 'Access denied'}</p>
              </div>
            </div>
          </div>
          
          <div className={`p-4 rounded-lg border ${canDeleteRoles ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex gap-3 items-center">
              {canDeleteRoles ? <CheckCircle className="w-5 h-5 text-green-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
              <div>
                <p className="text-sm font-medium">Delete Roles</p>
                <p className="text-xs text-gray-500">{canDeleteRoles ? 'Access granted' : 'Access denied'}</p>
              </div>
            </div>
          </div>
        </div> */}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={Users}
            title="Total Team Members"
            value={teamStats.total}
            subtitle="Including all members"
            color="blue"
          />
          <StatCard
            icon={UserCheck}
            title="Active Members"
            value={teamStats.active}
            subtitle="Currently active"
            color="green"
          />
          <StatCard
            icon={UserX}
            title="Inactive Members"
            value={teamStats.inactive}
            subtitle="Currently inactive"
            color="red"
          />
          <StatCard
            icon={Briefcase}
            title="Custom Roles"
            value={teamStats.roles}
            subtitle="Created by you"
            color="purple"
            onClick={handleManageRoles}
          />
        </div>

        {/* Main Content */}
        {requiresRoles ? (
          <EmptyState
            icon={Shield}
            title="No Roles Created Yet"
            description="You need to create at least one role before you can add team members to your organization."
            actionText="Create Your First Role"
            onAction={handleCreateRole}
          />
        ) : teamStats.total === 0 ? (
          <EmptyState
            icon={Users}
            title="No Team Members Yet"
            description="Start building your team by adding team members with specific roles and permissions."
            actionText="Add First Team Member"
            onAction={handleCreateTeamMember}
          />
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="p-6 border-b border-gray-100">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">
                    Team Members
                  </h2>
                  <p className="text-sm text-gray-500">
                    Manage your team members and their permissions
                  </p>
                </div>
                {hasRoles && (
                  <div className="flex gap-2 items-center text-sm text-gray-500">
                    <Crown className="w-4 h-4" />
                    <span>
                      {teamStats.roles} Custom Role
                      {teamStats.roles !== 1 ? "s" : ""}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <RolesTable handleEditModal={handleEditTeamMember} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Roles;
